import React, { useEffect, useState } from "react";

/**
 * PWA install banner.
 * - Listens for the browser's `beforeinstallprompt` (Chrome/Edge/Android).
 * - Shows a small bottom-anchored card that the user can dismiss for 7 days.
 * - On iOS Safari the event is unsupported, so we show a one-time hint with the
 *   "Tap Share → Add to Home Screen" instruction.
 */
const STORAGE_KEY = "ibloom_pwa_install_dismissed_at";
const DISMISS_DAYS = 7;

const isIos = () => /iPhone|iPad|iPod/i.test(navigator.userAgent);
const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches ||
  window.navigator.standalone === true;

const recentlyDismissed = () => {
  const ts = parseInt(localStorage.getItem(STORAGE_KEY) || "0", 10);
  return ts && Date.now() - ts < DISMISS_DAYS * 86400000;
};

export default function PwaInstallBanner() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone() || recentlyDismissed()) return;

    const onBip = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", onBip);

    // iOS doesn't fire BIP — show a manual hint after a short delay.
    if (isIos()) {
      const t = setTimeout(() => setIosHint(true), 4000);
      return () => { clearTimeout(t); window.removeEventListener("beforeinstallprompt", onBip); };
    }
    return () => window.removeEventListener("beforeinstallprompt", onBip);
  }, []);

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, String(Date.now()));
    setShow(false);
    setIosHint(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try {
      await deferred.userChoice;
    } catch { /* ignore */ }
    setDeferred(null);
    setShow(false);
  };

  if (!show && !iosHint) return null;

  // Position above the bottom nav (~64px) + safe area
  const wrapper = {
    position: "fixed", left: 12, right: 12,
    bottom: "calc(76px + env(safe-area-inset-bottom, 0px))",
    zIndex: 1100,
    background: "linear-gradient(135deg, #0F3D2E, #1C6B4A)",
    color: "white", borderRadius: 18, padding: 14,
    boxShadow: "0 18px 40px rgba(15,61,46,0.35)",
    display: "flex", gap: 12, alignItems: "center",
    fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
    animation: "ib-slide-up 0.3s ease-out",
  };

  return (
    <>
      <style>{`@keyframes ib-slide-up { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }`}</style>
      <div data-testid="pwa-install-banner" style={wrapper}>
        <img src="/icon-192.png" alt="Mapbazar" style={{ width: 44, height: 44, borderRadius: 12, background: "white", flexShrink: 0, padding: 4, boxSizing: "border-box" }} data-testid="pwa-install-logo" />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13.5, fontWeight: 700, lineHeight: 1.2 }}>Install Mapbazar</div>
          <div style={{ fontSize: 11.5, opacity: 0.85, marginTop: 3, lineHeight: 1.4 }}>
            {iosHint
              ? "Tap Share ⎙ → Add to Home Screen for one-tap access."
              : "Add to home screen — fast, offline-ready, no app store."}
          </div>
        </div>
        {!iosHint && (
          <button
            data-testid="pwa-install-btn"
            onClick={install}
            style={{ padding: "9px 14px", borderRadius: 100, border: "none", background: "white", color: "#0F3D2E", fontWeight: 800, fontSize: 12.5, cursor: "pointer", whiteSpace: "nowrap" }}
          >Install</button>
        )}
        <button
          data-testid="pwa-install-dismiss"
          onClick={dismiss}
          aria-label="Dismiss install prompt"
          style={{ padding: 6, borderRadius: "50%", border: "none", background: "rgba(255,255,255,0.18)", color: "white", fontSize: 14, cursor: "pointer", width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
        >✕</button>
      </div>
    </>
  );
}
